"use client";
import FlowArrowIcon from "@/shared/app/ui/icon/FlowArrowIcon";
import SectionTitle from "@/shared/app/ui/text/PageSectionTitle";

import { motion } from "framer-motion";
import Image from "next/image";

const FlowItem = ({
  title,
  bgColor,
  spanColor,
  direction = "right",
}: {
  title: string;
  bgColor: string;
  spanColor: string;
  direction?: "left" | "right";
}) => {
  const flexDirection = direction === "right" ? "flex-row-reverse" : "";
  return (
    <div
      className={`md:h-11 h-10 flex justify-between items-center px-3 md:py-1 py-[1px] my-1 rounded-full ${flexDirection}`}
      style={{ backgroundColor: bgColor }}
    >
      <span
        className="flex md:w-8 md:h-8  w-6 h-6 rounded-full justify-center items-center"
        style={{ backgroundColor: spanColor }}
      >
        <FlowArrowIcon color={bgColor} direction={direction} />
      </span>
      <p
        className="md:text-base text-sm  tracking-tight text-light-10 leading-4"
        style={{ fontFamily: '"paperlogy-Medium", sans-serif' }}
      >
        {title}
      </p>
    </div>
  );
};

const flowData = [
  {
    bg: "#FFF4F4",
    border: true,
    info: {
      number: "1",
      bgColor: "#FF5C5C",
      title: "친환경 상점",
      text: "지속가능한 소비를 실천하는 공간",
    },
    flows: [
      {
        title: "상품지급",
        bgColor: "#FF5C5C",
        spanColor: "#FFBDBD",
        direction: "right",
      },
      { type: "gap", h: "11" },
      { type: "gap", h: "11" },
      {
        title: "포인트 사용",
        bgColor: "#FF5C5C",
        spanColor: "#FFBDBD",
        direction: "left",
      },
    ],
  },
  {
    bg: "#FFF7F4",
    border: true,
    info: {
      number: "2",
      bgColor: "#FF7D5C",
      title: "사용자",
      text: "작은 실천이 만드는 큰 변화",
    },
    flows: [
      { type: "gap", h: "11" },
      { title: "플라스틱 배출", bgColor: "#FF7D5C", spanColor: "#FFC7BD" },
      {
        title: "포인트 지급",
        bgColor: "#FF7D5C",
        spanColor: "#FFC7BD",
        direction: "left",
      },
      { type: "gap", h: "11" },
    ],
  },
  {
    bg: "#F4FFF6",
    border: false,
    info: {
      number: "3",
      bgColor: "#3BD877",
      title: "플라스틱 정류장",
      text: "시민과 자원을 잇는 거점",
    },
    flows: [
      { type: "gap", h: "11" },
      { type: "gap", h: "11" },
      { title: "수거요청", bgColor: "#3BD877", spanColor: "#B3F9CD" },
      { type: "gap", h: "11" },
    ],
  },
  {
    bg: "#F4FEFF",
    border: true,
    info: {
      number: "4",
      bgColor: "#3B8FD8",
      title: "플라스틱 활용처",
      text: "수거된 플라스틱을 새 자원으로",
    },
    flows: [
      { type: "gap", h: "11" },
      { type: "gap", h: "11" },
      { type: "gap", h: "11" },
      {
        title: "방문수거",
        bgColor: "#3B8FD8",
        spanColor: "#9BD0FF",
        direction: "left",
      },
    ],
  },
];
const mobileTexts = [
  ["상점", "사용자"],
  ["사용자", "플라스틱 정류장"],
  ["플라스틱 정류장", "플라스틱 활용처"],
  ["플라스틱 정류장", "플라스틱 활용처"],
];

const InfoItem = ({
  number,
  bgColor,
  title,
  text,
}: {
  number: string | number;
  bgColor: string;
  title: string;
  text: string;
}) => {
  return (
    <div className="mt-10 md:mt-16 px-1 mb-5 md:mb-0">
      <span
        className="md:w-8 md:h-8 w-5 h-5 flex justify-center items-center rounded-full text-white text-sm md:text-base"
        style={{
          backgroundColor: bgColor,
          fontFamily: '"pre-Medium", sans-serif',
          lineHeight: "initial",
        }}
      >
        {number}
      </span>

      <p
        className="lg:text-2xl md:text-xl text-lg tracking-tighter text-black-200 md:mt-5 mt-3"
        style={{ fontFamily: '"pre-SemiBold", sans-serif' }}
      >
        {title}
      </p>

      <p
        className="text-lightGray-30 lg:text-base text-sm tracking-tight md:mt-1 mt-0 "
        style={{ fontFamily: '"pre-Medium", sans-serif' }}
      >
        {text}
      </p>
    </div>
  );
};
export default function SolutionGroGroSection() {
  return (
    <div className="bg-[#5acfb4] rounded-3xl md:rounded-[154px] my-5 md:my-20">
      <div className="w-full xl:max-w-[1280px] mx-auto px-5 xl:px-2 md:py-50 py-20">
        <SectionTitle
          align="center"
          label="Recycling Platform"
          title={<>자원순환 시민활동 관리 시스템</>}
          description={
            <>
              사용자, 상점, 플라스틱 정류장, 활용처를
              <br />
              연결하는 순환형 친환경 플랫폼
            </>
          }
          colorMode="light"
        />
        {/* 이미지 */}
        <div className="w-full max-w-full mx-auto mt-20 md:mt-30">
          <div className="grid gap-6 md:gap-10 grid-cols-1 md:grid-cols-[1.2fr_0.8fr]">
            {/* 왼쪽 큰 이미지 */}
            <motion.div
              className="relative rounded-4xl overflow-hidden"
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              viewport={{ once: true, amount: 0.3 }}
            >
              <div className="aspect-[798/1048] w-full">
                <Image
                  src="/assets/images/page/service/solution/grogro1.jpg"
                  alt="Solution 1"
                  fill
                  className="object-cover"
                />
              </div>
            </motion.div>

            {/* 오른쪽 영역 (세로로 두 칸) */}
            <div className="grid grid-rows-2 gap-6 md:gap-4">
              {/* 오른쪽 위 */}
              <motion.div
                className="relative rounded-4xl overflow-hidden aspect-[362/234] md:aspect-[526/500]"
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, ease: "easeOut", delay: 0.2 }}
                viewport={{ once: true, amount: 0.3 }}
              >
                <Image
                  src="/assets/images/page/service/solution/grogro2.jpg"
                  alt="Solution 2"
                  fill
                  className="object-cover"
                />
              </motion.div>

              {/* 오른쪽 아래 */}
              <motion.div
                className="relative rounded-4xl overflow-hidden aspect-[362/234] md:aspect-[526/520]"
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, ease: "easeOut", delay: 0.4 }}
                viewport={{ once: true, amount: 0.3 }}
              >
                <Image
                  src="/assets/images/page/service/solution/grogro3.jpg"
                  alt="Solution 3"
                  fill
                  className="object-cover"
                />
              </motion.div>
            </div>
          </div>
        </div>
        {/* 플로우 */}
        <div className="bg-white w-full mt-10 rounded-4xl md:py-24 lg:py-14 px-4 py-10">
          <SectionTitle
            align="center"
            label="Flow"
            title={<>어떻게 순환되나요?</>}
            description={<>사용자와 지역사회가 함께 만드는 순환 구조</>}
            colorMode="light"
          />
          {/* 그래프 */}
          <ul className="flex md:flex-row flex-col lg;px-14 md:px-4 px-0 md:mt-30 mb-4 mt-10">
            {flowData.map((col, i) => (
              <motion.li
                key={i}
                className="flex md:flex-col flex-col-reverse flex-1"
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, ease: "easeOut", delay: i * 0.2 }}
                viewport={{ once: true, amount: 0.3 }}
              >
                <div
                  className={`
        ${
          col.border
            ? "md:border-l md:border-r md:border-dashed md:border-[#BEBEBE]"
            : ""
        }
        ${col.bg ? `md:bg-[${col.bg}]` : ""}
      `}
                >
                  {col.flows.map((f: any, idx: number) =>
                    f.type === "gap" ? (
                      <div
                        key={idx}
                        className={`h-${f.h} my-1 hidden md:block`}
                      />
                    ) : (
                      <FlowItem
                        key={idx}
                        title={f.title}
                        bgColor={f.bgColor}
                        spanColor={f.spanColor}
                        direction={f.direction as "left" | "right" | undefined}
                      />
                    ),
                  )}

                  {/* ✅ md 이하에서만 표시되는 텍스트 */}
                  <div className="flex justify-between items-center text-sm font-medium text-gray-800 md:hidden mt-2 px-1">
                    {mobileTexts[i]?.map((text, idx) => (
                      <span
                        key={idx}
                        className="text-[13px] tracking-tight text-softGray-70"
                        style={{ fontFamily: '"pre-Medium", sans-serif' }}
                      >
                        {text}
                      </span>
                    ))}
                  </div>
                  {/*  */}
                </div>
                <InfoItem
                  number={col.info.number}
                  bgColor={col.info.bgColor}
                  title={col.info.title}
                  text={col.info.text}
                />
              </motion.li>
            ))}
          </ul>
          {/*  */}
        </div>
        {/*  */}
      </div>
    </div>
  );
}
